import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetWellsQuery } from "@api/mApi";
import { useGetWellsReportRecencyQuery } from "@api/pApi";
import { useAppDispatch } from "@app/hooks";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { CAPABILITIES } from "@api/capabilities";
import { selectWell } from "@app/slices/wellContextSlice";
import { CreateWorkoverModal } from "@features/createWorkover/CreateWorkoverModal";
import { ImportExcelModal } from "@features/createWorkover/ImportExcelModal";
import { Popover } from "@components/input";
import { useLocale } from "@hooks/useLocale";
import { WellMap } from "@components/map/WellMap";
import { QueryState } from "@components/ui/QueryState";
import { useUnits } from "@units/useUnits";
import { STATUS_KEY as STATUS_MSG } from "@features/wellRecord/wellLabels";
import type { WellView, WellStatus } from "@api/types";

function uniq(xs: string[]): string[] {
  return Array.from(new Set(xs.filter((x) => x && x !== "—"))).sort();
}

/**
 * Well Explorer — the proof-of-stack screen. Spatial-first: a real Leaflet map
 * beside a filterable register table. Reads the well register from Oxbo-M
 * (served by MSW in standalone mode, or the live m.dev service). The M contract
 * filters server-side on `status`; the finer field/basin/operator/search
 * filters are applied client-side over the register (which is small and cached).
 * Clicking a well selects it and drops into its record.
 */
export function WellExplorer() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Register TD is stored in metres; render it in the viewer's display profile.
  const { convertField, fmt } = useUnits();

  // "Crear / Importar" menu — now capability-gated (§ACL). Each action checks
  // its own capability: Crear Workover / Crear Drilling need well:create (the
  // Drilling path also creates an event → event:create); Importar needs
  // import:run. The menu trigger shows if the user holds ANY of the three.
  const { can } = useCapabilities();
  const canWell = can(CAPABILITIES.wellCreate);
  const canEvent = can(CAPABILITIES.eventCreate);
  const canImport = can(CAPABILITIES.importRun);
  const canCreateWorkover = canWell;
  const canCreateDrilling = canWell || canEvent;
  // Completion / Intervention create a well (optional) + an event, same as the
  // other create actions → gated on well:create OR event:create.
  const canCreateCompletion = canWell || canEvent;
  const canCreateIntervention = canWell || canEvent;
  const showCreateMenu =
    canCreateWorkover ||
    canCreateDrilling ||
    canCreateCompletion ||
    canCreateIntervention ||
    canImport;
  const createBtnRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [workoverOpen, setWorkoverOpen] = useState(false);
  const [drillingOpen, setDrillingOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [interventionOpen, setInterventionOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [field, setField] = useState("");
  const [basin, setBasin] = useState("");
  const [operator, setOperator] = useState("");
  const [status, setStatus] = useState("");

  // The register itself is fetched unfiltered (small, cached); we derive filter
  // option lists from it and apply all filters client-side for instant feedback.
  const { data: allWells = [], isLoading, isError, refetch } = useGetWellsQuery();
  // Operational recency (latest daily-report date per well) for the default
  // sort. Tolerant: on error/empty, `recency` is [] and the order is unchanged.
  const { data: recency = [] } = useGetWellsReportRecencyQuery();
  const recencyByUwi = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of recency) if (r.last_report) m.set(r.well_uwi, r.last_report);
    return m;
  }, [recency]);
  // The single well with the most recent activity overall — the "where to go" hint.
  const mostRecentUwi = useMemo(() => {
    let best: string | undefined, bestDate = "";
    recencyByUwi.forEach((d, uwi) => {
      if (d > bestDate) { bestDate = d; best = uwi; }
    });
    return best;
  }, [recencyByUwi]);

  const fields = useMemo(() => uniq(allWells.map((w) => w.field)), [allWells]);
  const basins = useMemo(() => uniq(allWells.map((w) => w.basin)), [allWells]);
  const operators = useMemo(() => uniq(allWells.map((w) => w.operator)), [allWells]);

  const wells = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allWells.filter((w) => {
      if (q && !`${w.commonName} ${w.uwi} ${w.apiNo ?? ""} ${w.field}`.toLowerCase().includes(q))
        return false;
      if (field && w.field !== field) return false;
      if (basin && w.basin !== basin) return false;
      if (operator && w.operator !== operator) return false;
      if (status && w.status !== status) return false;
      return true;
    });
    const rank = (w: WellView) => recencyByUwi.get(w.uwi) ?? "";
    return [...filtered].sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra < rb ? 1 : -1; // most-recent report date first
      return a.commonName.localeCompare(b.commonName);
    });
  }, [allWells, search, field, basin, operator, status, recencyByUwi]);

  const openWell = (w: WellView) => {
    dispatch(selectWell(w.id));
    navigate("/well");
  };

  const clearFilters = () => {
    setSearch("");
    setField("");
    setBasin("");
    setOperator("");
    setStatus("");
  };

  return (
    <div>
      <div
        className="page-head"
        style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}
      >
        <div>
          <h1>{t("explorer.title")}</h1>
          <p className="subtitle">{t("explorer.subtitle")}</p>
        </div>
        {showCreateMenu && (
          <div style={{ position: "relative" }}>
            <button
              ref={createBtnRef}
              type="button"
              className="btn primary"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {t("createmenu.button")} ▾
            </button>
            <Popover
              anchorRef={createBtnRef}
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              role="menu"
              aria-label={t("createmenu.button")}
              minWidth={220}
              className="picker-pop"
            >
              <div role="none" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {canCreateWorkover ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                    onClick={() => {
                      setMenuOpen(false);
                      setWorkoverOpen(true);
                    }}
                  >
                    {t("createwo.button")}
                  </button>
                ) : null}
                {canCreateDrilling ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                    onClick={() => {
                      setMenuOpen(false);
                      setDrillingOpen(true);
                    }}
                  >
                    {t("createdr.button")}
                  </button>
                ) : null}
                {canCreateCompletion ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                    onClick={() => {
                      setMenuOpen(false);
                      setCompletionOpen(true);
                    }}
                  >
                    {t("createcomp.button")}
                  </button>
                ) : null}
                {canCreateIntervention ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                    onClick={() => {
                      setMenuOpen(false);
                      setInterventionOpen(true);
                    }}
                  >
                    {t("createint.button")}
                  </button>
                ) : null}
                {canImport ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="btn"
                    style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                    onClick={() => {
                      setMenuOpen(false);
                      setImportOpen(true);
                    }}
                  >
                    {t("import.button")}
                  </button>
                ) : null}
              </div>
            </Popover>
          </div>
        )}
      </div>

      {showCreateMenu && (
        <>
          <CreateWorkoverModal
            open={workoverOpen}
            onClose={() => setWorkoverOpen(false)}
            eventType="workover"
          />
          <CreateWorkoverModal
            open={drillingOpen}
            onClose={() => setDrillingOpen(false)}
            eventType="drilling"
          />
          <CreateWorkoverModal
            open={completionOpen}
            onClose={() => setCompletionOpen(false)}
            eventType="completion"
          />
          <CreateWorkoverModal
            open={interventionOpen}
            onClose={() => setInterventionOpen(false)}
            eventType="intervention"
          />
          <ImportExcelModal open={importOpen} onClose={() => setImportOpen(false)} />
        </>
      )}

      {/* Data-lineage banner: hidden unless the admin overlay (.lineage-on) is set. */}
      <div className="lineage-banner" role="note">
        <span aria-hidden="true">●</span>
        {t("topbar.lineage")} — {t("topbar.lineage.adminOnly")}
      </div>

      <div className="filterbar">
        <div className="field" style={{ flex: "1 1 220px" }}>
          <label htmlFor="wx-search">{t("explorer.filter.field")}</label>
          <input
            id="wx-search"
            type="search"
            placeholder={t("explorer.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t("explorer.search")}
          />
        </div>
        <div className="field">
          <label htmlFor="wx-field">{t("explorer.filter.field")}</label>
          <select id="wx-field" value={field} onChange={(e) => setField(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {fields.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-basin">{t("explorer.filter.basin")}</label>
          <select id="wx-basin" value={basin} onChange={(e) => setBasin(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {basins.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-operator">{t("explorer.filter.operator")}</label>
          <select id="wx-operator" value={operator} onChange={(e) => setOperator(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {operators.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wx-status">{t("explorer.filter.status")}</label>
          <select id="wx-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("explorer.filter.all")}</option>
            {(["drilling", "completion", "producing", "suspended"] as WellStatus[]).map((s) => (
              <option key={s} value={s}>
                {t(STATUS_MSG[s])}
              </option>
            ))}
          </select>
        </div>
        <span className="result-count">{t("explorer.count", { n: wells.length })}</span>
      </div>

      <div className="explorer-split">
        <div className="map-card">
          <WellMap wells={wells} onSelect={openWell} ariaLabel={t("explorer.map.placeholder")} />
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="table-scroll">
            <QueryState
              isLoading={isLoading}
              isError={isError}
              isEmpty={wells.length === 0}
              emptyMessage={t("explorer.empty")}
              onRetry={refetch}
            >
              <table>
                <thead>
                  <tr>
                    <th>{t("explorer.col.well")}</th>
                    <th>{t("explorer.col.field")}</th>
                    <th>{t("explorer.col.operator")}</th>
                    <th>{t("explorer.col.status")}</th>
                    <th>{t("explorer.col.lastActivity")}</th>
                    <th className="num">{t("explorer.col.td")}</th>
                  </tr>
                </thead>
                <tbody>
                  {wells.map((w) => (
                    <tr
                      key={w.id}
                      className={w.uwi === mostRecentUwi ? "selectable recent" : "selectable"}
                      data-owner="m"
                      onClick={() => openWell(w)}
                    >
                      <td>
                        <span className="well-link">
                          {w.uwi === mostRecentUwi && (
                            <span className="well-dot" aria-hidden="true">●</span>
                          )}
                          {w.commonName}
                        </span>
                        <div className="mono" style={{ fontSize: 11, color: "var(--dim)" }}>
                          {w.apiNo ?? w.uwi}
                        </div>
                      </td>
                      <td>{w.field}</td>
                      <td>{w.operator}</td>
                      <td>
                        {w.status ? (
                          <span className={`badge status-${w.status}`}>
                            {t(STATUS_MSG[w.status])}
                          </span>
                        ) : (
                          <span className="badge">{w.statusRaw ?? "—"}</span>
                        )}
                      </td>
                      <td className="tab-nums">{recencyByUwi.get(w.uwi) ?? "—"}</td>
                      <td className="num tab-nums">
                        {fmt(convertField("daily_report", "md_current", w.totalDepthM, "metric"))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </QueryState>
          </div>
        </div>
      </div>

      <button type="button" className="btn" style={{ marginTop: 12 }} onClick={clearFilters}>
        {t("explorer.clearFilters")}
      </button>
    </div>
  );
}
